import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

// ── Types ────────────────────────────────────────────────────────────────────

export type ErrorLevel = 'error' | 'warn' | 'info' | 'debug';

export interface ErrorLog {
  id: string;
  timestamp: string;
  level: ErrorLevel;
  service: string;
  message: string;
  traceId: string;
  stack?: string;
  metadata?: Record<string, unknown>;
  requestId?: string;
  userId?: string;
  tenantId?: string;
  duration?: number;
  httpStatus?: number;
  httpMethod?: string;
  httpPath?: string;
}

export interface PaginatedErrorLogs {
  data: ErrorLog[];
  total: number;
  page: number;
  limit: number;
}

export interface ErrorLogFilters {
  level?: string; // ALL | error | warn | info | debug
  service?: string; // ALL | service name
  period?: string; // 24h | 7d | 30d | custom
  dateFrom?: string;
  dateTo?: string;
  search?: string; // traceId / mensagem
  page?: number;
  limit?: number;
}

// ── Query keys ───────────────────────────────────────────────────────────────

export const errorLogsKeys = {
  all: ['admin', 'error-logs'] as const,
  list: (filters?: ErrorLogFilters) => ['admin', 'error-logs', 'list', filters ?? {}] as const,
};

// ── Hook (backend real; erros HTTP viram estado de erro, sem fallback) ──

export function useErrorLogs(filters?: ErrorLogFilters) {
  return useQuery<PaginatedErrorLogs>({
    queryKey: errorLogsKeys.list(filters),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (filters?.level && filters.level !== 'ALL') params['level'] = filters.level;
      if (filters?.service && filters.service !== 'ALL') params['service'] = filters.service;
      if (filters?.period && filters.period !== 'ALL' && filters.period !== 'custom') params['period'] = filters.period;
      if (filters?.dateFrom) params['dateFrom'] = filters.dateFrom;
      if (filters?.dateTo) params['dateTo'] = filters.dateTo;
      if (filters?.search?.trim()) params['search'] = filters.search.trim();
      params['page'] = String(filters?.page ?? 1);
      params['limit'] = String(filters?.limit ?? 20);

      const res = await apiClient.get<PaginatedErrorLogs>('/admin/error-logs', { params });
      // API may return { data, total, page, limit } or { data, meta } — normalizar
      const raw = res as unknown as Record<string, unknown>;
      if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: unknown }).data)) {
        // Se vier wrapper { data: [...], total, ... } direto ok
        if ('total' in raw) return res;
        // Se vier { data: [...], meta: { total, page, limit } }
        if ('meta' in raw) {
          const meta = (raw as { meta: { total: number; page: number; limit: number } }).meta;
          return { data: (raw as { data: ErrorLog[] }).data, total: meta.total, page: meta.page, limit: meta.limit };
        }
      }
      // Se vier { items/data } com shape diferente, tentar adaptar
      if (raw && 'items' in raw) {
        const items = (raw as { items: ErrorLog[] }).items;
        const total = (raw as { total?: number }).total ?? items.length;
        return { data: items, total, page: Number(params['page']), limit: Number(params['limit']) };
      }
      return res;
    },
    placeholderData: (prev) => prev,
  });
}
